import { ref, computed, watch, onScopeDispose, type Ref, type ComputedRef } from 'vue';
import type { ConnectionErrorType } from '@/components/ConnectionErrorOverlay.vue';
import type { AppGameState } from './useGameFlow';
import type { ConnectionStatus } from './webSocketSingleton';
import { getMetrics } from '@/telemetry/metrics';
import { reportClientError, ErrorLocation } from '@/telemetry/errors';

/**
 * Delay before the overlay becomes visible after a disconnect. Tuned to be
 * comfortably longer than a graceful handoff (server preStop + reconnect =
 * ~150–400 ms in practice) plus the slower transient reconnects that still
 * complete within a couple of seconds, so the overlay does not flash on a
 * brief blip and then vanish (#290). Real outages are still surfaced well
 * within the 15s abort window below. See #207.
 */
const OVERLAY_DEBOUNCE_MS = 2000;

export interface UseConnectionErrorHandlingOptions {
  gameState: Readonly<Ref<AppGameState>>;
  connectionStatus: Readonly<Ref<ConnectionStatus>>;
  lastError: Readonly<Ref<string | null>>;
  reconnectAttempt: Readonly<Ref<number>>;
  maxReconnectAttempts: Readonly<Ref<number>>;
  manualReconnect: () => Promise<void>;
  handleError: (error: string) => void;
}

export interface UseConnectionErrorHandlingReturn {
  connectionErrorType: ComputedRef<ConnectionErrorType>;
  reconnectionState: ComputedRef<{
    isReconnecting: boolean;
    attempt: number;
    maxAttempts: number;
  }>;
  isConnectionErrorVisible: ComputedRef<boolean>;
  handleRetryConnection: () => Promise<void>;
}

export function useConnectionErrorHandling(
  options: UseConnectionErrorHandlingOptions
): UseConnectionErrorHandlingReturn {
  const {
    gameState,
    connectionStatus,
    lastError,
    reconnectAttempt,
    maxReconnectAttempts,
    manualReconnect,
    handleError,
  } = options;

  const connectionErrorType = computed((): ConnectionErrorType => {
    if (!lastError.value || typeof lastError.value !== 'string') return 'connection_lost';

    const error = lastError.value.toLowerCase();
    if (error.includes('timeout')) return 'server_timeout';
    if (error.includes('sync')) return 'sync_failed';
    if (error.includes('connection') || error.includes('websocket')) return 'connection_lost';
    return 'unknown';
  });

  const reconnectionState = computed(() => ({
    isReconnecting: connectionStatus.value === 'connecting' && reconnectAttempt.value > 0,
    attempt: reconnectAttempt.value,
    maxAttempts: maxReconnectAttempts.value,
  }));

  // Raw "should-be-visible" condition. The exposed `isConnectionErrorVisible`
  // below is a debounced view of this so a brief disconnect during a rolling
  // deploy does not flash the overlay (#207). A real outage flips the debounced
  // visibility after OVERLAY_DEBOUNCE_MS.
  const rawErrorVisible = computed((): boolean => {
    if (gameState.value !== 'in-progress') return false;
    return (
      (connectionStatus.value === 'error' && !!lastError.value) ||
      connectionStatus.value === 'disconnected' ||
      (connectionStatus.value === 'connecting' && reconnectAttempt.value > 0)
    );
  });

  const debouncedErrorVisible = ref(false);
  let debounceTimeoutId: ReturnType<typeof setTimeout> | null = null;

  watch(
    rawErrorVisible,
    (raw, prev) => {
      if (raw === prev) return;
      if (raw) {
        if (debounceTimeoutId === null) {
          debounceTimeoutId = setTimeout(() => {
            debounceTimeoutId = null;
            // Re-check the raw condition at fire time — it might have cleared
            // during the debounce window without our watcher firing for the
            // false transition (e.g. very fast reconnect).
            if (rawErrorVisible.value) {
              debouncedErrorVisible.value = true;
            }
          }, OVERLAY_DEBOUNCE_MS);
        }
      } else {
        if (debounceTimeoutId !== null) {
          clearTimeout(debounceTimeoutId);
          debounceTimeoutId = null;
        }
        debouncedErrorVisible.value = false;
      }
    },
    { immediate: true }
  );

  const isConnectionErrorVisible = computed(() => debouncedErrorVisible.value);

  let abortTimeoutId: ReturnType<typeof setTimeout> | null = null;
  let overlayShownAt: number | null = null;

  const handleRetryConnection = async () => {
    try {
      await manualReconnect();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Manual reconnection failed';
      reportClientError(error, ErrorLocation.ConnectionManualReconnect);
      handleError(message);
    }
  };

  // Watch for WebSocket connection errors outside in-progress phase
  watch([connectionStatus, lastError], ([status, error]) => {
    if (status === 'error' && error && gameState.value !== 'in-progress') {
      handleError(error);
    }
  });

  // Abort timeout: if overlay stays visible for 15s, abort the game
  watch(isConnectionErrorVisible, visible => {
    if (visible) {
      getMetrics().uiConnectionErrorOverlayShown.add(1);
      overlayShownAt = Date.now();
      abortTimeoutId = setTimeout(() => {
        if (isConnectionErrorVisible.value) {
          handleError('Connection lost. Game aborted.');
        }
      }, 15000);
    } else {
      if (overlayShownAt) {
        getMetrics().uiConnectionErrorOverlayDurationMs.record(Date.now() - overlayShownAt);
        overlayShownAt = null;
      }
      if (abortTimeoutId) {
        clearTimeout(abortTimeoutId);
        abortTimeoutId = null;
      }
    }
  });

  onScopeDispose(() => {
    if (abortTimeoutId) {
      clearTimeout(abortTimeoutId);
      abortTimeoutId = null;
    }
    if (debounceTimeoutId !== null) {
      clearTimeout(debounceTimeoutId);
      debounceTimeoutId = null;
    }
  });

  return {
    connectionErrorType,
    reconnectionState,
    isConnectionErrorVisible,
    handleRetryConnection,
  };
}
